import { sectionRenderer, buildSectionRenderingURL, morphSection, normalizeSectionId } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import {
  createLatestWriteGate,
  debounce,
  formatMoney,
  isAbortError,
  prefersReducedMotion,
  startViewTransition,
} from '@theme/utilities';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { applySeeResultsCount, setSeeResultsPending } from '@theme/facet-index-rail';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/** Extra ruimte onder de sticky header (thema --scroll-margin, anders fallback). */
const FILTER_SCROLL_EXTRA_GAP_FALLBACK_PX = 32;

/** Custom customer event voor filtergebruik (SHO-9289). */
const HDS_FILTER_APPLIED_EVENT = 'hds:filter_applied';
const SORTING_MOBILE_BREAKPOINT = 750;
const HDS_FILTER_APPLIED_DEBOUNCE_MS = 500;
/** Wacht kort tot klikken stoppen voordat we de section/drawer opnieuw fetchen. */
export const FILTER_SECTION_DEBOUNCE_MS = 120;
const HDS_FILTER_PUBLISH_RETRY_MS = 50;
const HDS_FILTER_PUBLISH_DEADLINE_MS = 8000;

/** Sectiebestand van de verticale desktop-filterlijst (zie assets/hds-lazy-vertical-facets.js). */
const VERTICAL_FACETS_SECTION = 'collection-facets-vertical';
const VERTICAL_FACETS_DESKTOP = '(min-width: 750px)';

/**
 * Directe feedback bij een filterklik: het raster wordt gedimd tot de morph het vervangt.
 * @param {string} sectionId
 * @param {boolean} pending
 */
function setResultsPending(sectionId, pending) {
  document
    .getElementById(`shopify-section-${normalizeSectionId(sectionId)}`)
    ?.classList.toggle('hds-results-pending', pending);
}

/** @returns {boolean} of er een verticale filterkolom is die we mee moeten verversen */
function wantsVerticalFacets() {
  return window.matchMedia(VERTICAL_FACETS_DESKTOP).matches && Boolean(document.querySelector('[data-hds-vertical-filters]'));
}

/**
 * De secties die met het raster mee moeten: de mobiele drawer (bij filteren daarin) en de
 * desktopkolom. Klik en voorladen gebruiken dezelfde lijst, anders verschilt de cache-sleutel.
 * @param {boolean} inDrawer
 * @returns {{ drawerRoot: HTMLElement | null, drawerSection: string, verticalSection: string, companions: string[] }}
 */
function facetCompanions(inDrawer) {
  const root = inDrawer ? document.querySelector('#filters-drawer [data-facets-drawer-root]') : null;
  const drawerRoot = root instanceof HTMLElement ? root : null;
  const drawerSection = drawerRoot ? drawerRoot.dataset.facetsDrawerSectionId || 'collection-facets-drawer' : '';
  const verticalSection = wantsVerticalFacets() ? VERTICAL_FACETS_SECTION : '';
  return { drawerRoot, drawerSection, verticalSection, companions: [drawerSection, verticalSection].filter(Boolean) };
}

/**
 * Zet de filterlijst uit het meegekomen sectiedeel in de kolom. De wrapper heeft
 * data-skip-subtree-update, dus de sectiemorph laat de oude lijst staan tot hier; morph in
 * plaats van innerHTML houdt focus en uitgeklapte filters vast. Zonder deel maken we de
 * kolom leeg, dan haalt hds-lazy-vertical-facets.js hem zelf opnieuw op.
 * @param {string | null} html
 */
function applyVerticalFacets(html) {
  const lists = document.querySelectorAll('[data-hds-vertical-filters]');
  if (!lists.length) return;

  const section = html
    ? new DOMParser().parseFromString(html, 'text/html').querySelector('[id^="shopify-section-"]')
    : null;

  for (const el of lists) {
    if (!(el instanceof HTMLElement)) continue;
    if (!section) {
      el.replaceChildren();
      continue;
    }
    const next = /** @type {HTMLElement} */ (el.cloneNode(false));
    next.innerHTML = section.innerHTML.split(FACETS_DRAWER_MAIN_TOKEN).join(el.dataset.mainSectionId || '');
    // morph slaat de subtree over als beide kanten het attribuut hebben; hier willen we hem juist bijwerken.
    el.removeAttribute('data-skip-subtree-update');
    next.removeAttribute('data-skip-subtree-update');
    morph(el, next);
    el.setAttribute('data-skip-subtree-update', 'true');
    el.dataset.loadedKey = `${window.location.pathname}${window.location.search}`;
  }
}

/** @type {Map<string, ReturnType<typeof createLatestWriteGate>>} */
const facetsSectionUpdateGates = new Map();

/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const facetsSectionUpdateTimers = new Map();

/** @type {Set<AbortController>} */
const facetsDrawerAbortControllers = new Set();

/**
 * @param {string} sectionId
 * @returns {ReturnType<typeof createLatestWriteGate>}
 */
function getFacetsSectionUpdateGate(sectionId) {
  let gate = facetsSectionUpdateGates.get(sectionId);
  if (!gate) {
    gate = createLatestWriteGate();
    facetsSectionUpdateGates.set(sectionId, gate);
  }
  return gate;
}

/**
 * @param {string} sectionId
 * @returns {number}
 */
export function nextFacetsSectionUpdateGeneration(sectionId) {
  return getFacetsSectionUpdateGate(sectionId).next();
}

/**
 * @param {string} sectionId
 * @param {number} generation
 * @returns {boolean}
 */
export function isCurrentFacetsSectionUpdate(sectionId, generation) {
  return getFacetsSectionUpdateGate(sectionId).isCurrent(generation);
}

/**
 * @param {string} sectionId
 * @param {() => void} callback
 */
export function scheduleFacetsSectionUpdate(sectionId, callback, delay = FILTER_SECTION_DEBOUNCE_MS) {
  const previous = facetsSectionUpdateTimers.get(sectionId);
  if (previous !== undefined) clearTimeout(previous);

  const timer = setTimeout(() => {
    facetsSectionUpdateTimers.delete(sectionId);
    callback();
  }, delay);

  facetsSectionUpdateTimers.set(sectionId, timer);
}

/**
 * Breekt in-flight drawer fetches af zodat stale HTML de sheet niet overschrijft.
 */
export function abortFacetsDrawerLoads() {
  for (const controller of facetsDrawerAbortControllers) {
    controller.abort();
  }
  facetsDrawerAbortControllers.clear();
}

/**
 * @typedef {'add' | 'remove' | 'clear'} HdsFilterAction
 * @typedef {'mobile_top_bar' | 'drawer' | 'desktop'} HdsFilterSource
 * @typedef {{ filter_name: string, filter_value: string, filter_action: HdsFilterAction, filter_source?: HdsFilterSource }} HdsFilterAppliedContext
 * @typedef {HdsFilterAppliedContext & { results_count: number, collection_handle: string, filter_source: HdsFilterSource }} HdsFilterAppliedPayload
 */

/**
 * Zoekresultaten hebben geen collectie. Zonder markering vallen die filterklikken
 * in analyses weg als "ontbrekende handle", wat eerder tot een te somber beeld van
 * de meetdekking leidde (SHO-9994). Vandaar een expliciete sentinel.
 */
export const SEARCH_COLLECTION_HANDLE = 'search';

/**
 * @param {string} [pathname]
 * @returns {string} collectie-handle, `search` op de zoekpagina, anders leeg
 */
export function getCollectionHandleFromPath(pathname = window.location.pathname) {
  const match = pathname.match(/\/collections\/([^/?#]+)/);
  if (!match?.[1]) {
    return /(?:^|\/)search(?:[/?#]|$)/.test(pathname) ? SEARCH_COLLECTION_HANDLE : '';
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Prefer the Liquid handle on the form / results list so the pixel is not tied to
 * sandbox or locale path parsing (SHO-9994).
 *
 * @param {Element | null | undefined} fromEl
 * @returns {string}
 */
export function readCollectionHandle(fromEl) {
  if (fromEl instanceof Element) {
    const fromHost = fromEl.getAttribute('data-hds-collection-handle')?.trim();
    if (fromHost) return fromHost;
    const closest = fromEl.closest('[data-hds-collection-handle]');
    const fromClosest = closest?.getAttribute('data-hds-collection-handle')?.trim();
    if (fromClosest) return fromClosest;
  }

  const doc = fromEl instanceof Element ? fromEl.ownerDocument : document;
  const fromResults =
    doc.getElementById('ResultsList')?.getAttribute('data-hds-collection-handle')?.trim() ||
    doc.querySelector('[data-hds-collection-handle]')?.getAttribute('data-hds-collection-handle')?.trim();
  if (fromResults) return fromResults;

  return getCollectionHandleFromPath(doc.defaultView?.location?.pathname ?? window.location.pathname);
}

/**
 * Capture while the form is still connected. After a drawer innerHTML swap the node
 * is detached and `closest('dialog')` would wrongly look like desktop (SHO-9912/9994).
 *
 * @param {Element | null | undefined} fromEl
 * @returns {HdsFilterSource}
 */
export function getFilterSource(fromEl) {
  const doc = fromEl instanceof Element ? fromEl.ownerDocument : document;
  const inDialog = Boolean(fromEl instanceof Element && fromEl.closest('dialog'));
  const hasChipRow = Boolean(doc.querySelector('.facets-chip-row'));
  if (inDialog) return hasChipRow ? 'mobile_top_bar' : 'drawer';
  return 'desktop';
}

/**
 * Strip trailing product-count suffix like " (12)" from facet labels.
 * @param {string} label
 * @returns {string}
 */
function stripFilterCountSuffix(label) {
  return label.replace(/\s*\(\d[\d.,\s]*\)\s*$/u, '').trim();
}

/**
 * Readable facet group label (never the metaobject gid).
 * @param {Element} fromEl
 * @returns {string}
 */
function getFacetFilterName(fromEl) {
  const details = fromEl.closest('details');
  const label = details?.querySelector('.facets__label');
  return label?.textContent?.trim() ?? '';
}

/**
 * Readable facet value from the toggled input's label (never input.value / gid).
 * @param {HTMLInputElement} input
 * @returns {string}
 */
function getFacetFilterValueFromInput(input) {
  const item =
    input.closest('li, .facets__item, fieldset, .facets__item-label, label') ?? input.parentElement;
  const textEl = item?.querySelector?.('.facets__facet-label-text');
  if (textEl?.textContent) return stripFilterCountSuffix(textEl.textContent.trim());

  if (input.dataset.label) return stripFilterCountSuffix(input.dataset.label.trim());

  const aria = input.getAttribute('aria-label');
  if (aria) return stripFilterCountSuffix(aria.trim());

  return '';
}

/**
 * Product count after section morph.
 * @param {string} sectionId
 * @returns {number}
 */
export function readResultsCount(sectionId) {
  const resultsList = getResultsListTarget(sectionId);
  const scope =
    resultsList?.closest('.shopify-section') ??
    document.getElementById(`shopify-section-${sectionId}`) ??
    document;

  const grid = scope.querySelector('[data-total-items]');
  if (grid) {
    const total = Number(grid.getAttribute('data-total-items'));
    if (Number.isFinite(total) && total >= 0) return total;
  }

  const countEl = scope.querySelector('[data-testid="products-count"] span');
  const raw = countEl?.textContent ?? '';
  const digits = raw.replace(/[^\d]/g, '');
  if (digits) {
    const parsed = Number(digits);
    if (Number.isFinite(parsed)) return parsed;
  }

  return 0;
}

/**
 * Copies the on-page product count onto the drawer CTA. Section morph skips the lazy
 * drawer body, and a drawer refetch still ships the unfiltered `collection.products_count`.
 *
 * @param {string} sectionId
 */
export function syncFacetsSeeResultsCount(sectionId) {
  applySeeResultsCount(readResultsCount(sectionId), document.documentElement.lang || 'nl');
}

/**
 * @param {HdsFilterAppliedPayload} payload
 */
function publishHdsFilterApplied(payload) {
  const deadline = Date.now() + HDS_FILTER_PUBLISH_DEADLINE_MS;

  const tick = () => {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish === 'function') {
      publish(HDS_FILTER_APPLIED_EVENT, payload);
      return;
    }
    if (Date.now() > deadline) return;
    setTimeout(tick, HDS_FILTER_PUBLISH_RETRY_MS);
  };

  tick();
}

/**
 * @param {Element} removeComponent
 * @param {Event} [event]
 * @returns {HdsFilterAppliedContext}
 */
function getFilterContextFromRemoveComponent(removeComponent, event) {
  const clearAll =
    event?.target instanceof Element
      ? event.target.closest('.facets__clear-all')
      : removeComponent.querySelector('.facets__clear-all');

  if (clearAll) {
    return { filter_name: '', filter_value: '', filter_action: 'clear' };
  }

  const clone = /** @type {HTMLElement} */ (removeComponent.cloneNode(true));
  clone.querySelectorAll('.svg-wrapper, .visually-hidden, .swatch').forEach((el) => el.remove());
  const text = clone.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  const colonIdx = text.indexOf(':');
  if (colonIdx > 0) {
    return {
      filter_name: text.slice(0, colonIdx).trim(),
      filter_value: text.slice(colonIdx + 1).trim(),
      filter_action: 'remove',
    };
  }

  return { filter_name: '', filter_value: text, filter_action: 'remove' };
}

/**
 * Resolve the facet checkbox/radio that actually changed.
 *
 * Horizon's `on:change` proxy rewrites `event.target` to the element that has the
 * `on:change` attribute (often the whole `facet-inputs-component`). Falling back to
 * `querySelector('input')` then returns the *first* filter in the group — wrong value
 * and often `checked === false` → spurious `remove` (SHO-9289).
 *
 * Prefer `composedPath()` (still the real input under the proxy), then label/control.
 *
 * @param {Event | { target?: EventTarget | null, composedPath?: () => EventTarget[] } | undefined} event
 * @returns {HTMLInputElement | null}
 */
export function resolveFacetInputFromEvent(event) {
  const path = typeof event?.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (node instanceof HTMLInputElement && node.name.startsWith('filter.')) {
      return node;
    }
  }

  const target = event?.target;
  if (target instanceof HTMLInputElement && target.name.startsWith('filter.')) return target;
  if (!(target instanceof Element)) return null;

  if (target instanceof HTMLLabelElement) {
    const control = target.control;
    if (control instanceof HTMLInputElement && control.name.startsWith('filter.')) return control;
  }

  const closest = target.closest('label');
  if (closest instanceof HTMLLabelElement && closest.control instanceof HTMLInputElement) {
    const control = closest.control;
    if (control.name.startsWith('filter.')) return control;
  }

  // Never querySelector the whole facet-inputs-component — that picks the first checkbox.
  return null;
}

/**
 * @returns {number}
 */
function getStickyTopOffsetFromCss() {
  const styles = getComputedStyle(document.body);
  const headerHeight = parseFloat(styles.getPropertyValue('--header-height')) || 0;
  const announcement = parseFloat(styles.getPropertyValue('--announcement-bar-height-above')) || 0;
  return headerHeight + announcement;
}

/**
 * @returns {number}
 */
function getFilterScrollExtraGap() {
  const rootStyles = getComputedStyle(document.documentElement);
  const scrollMargin = parseFloat(rootStyles.getPropertyValue('--scroll-margin'));
  if (!Number.isNaN(scrollMargin) && scrollMargin > 0) return scrollMargin;
  return FILTER_SCROLL_EXTRA_GAP_FALLBACK_PX;
}

/**
 * Zichtbare sticky header + extra marge zodat het raster niet tegen de header plakt.
 * @returns {number}
 */
function getFilterScrollOffset() {
  let offset = getStickyTopOffsetFromCss();

  const header = document.querySelector('header-component');
  if (header instanceof HTMLElement && header.hasAttribute('sticky')) {
    const headerSection = header.closest('.header-section') ?? header;
    const headerBottom = headerSection.getBoundingClientRect().bottom;
    if (headerBottom > 0) {
      offset = Math.max(offset, headerBottom);
    }
  }

  return offset + getFilterScrollExtraGap();
}

/**
 * @returns {ScrollBehavior}
 */
function getFilterScrollBehavior() {
  return prefersReducedMotion() ? 'instant' : 'smooth';
}

/**
 * @param {string} sectionId
 * @returns {HTMLElement | null}
 */
function getResultsListTarget(sectionId) {
  const byId = document.getElementById('ResultsList');
  if (byId instanceof HTMLElement) return byId;

  const bySection = document.querySelector(`results-list[section-id="${CSS.escape(sectionId)}"]`);
  return bySection instanceof HTMLElement ? bySection : null;
}

/**
 * @param {HTMLElement} target
 * @param {ScrollBehavior} behavior
 */
function nudgeScrollBelowStickyHeader(target, behavior) {
  const header = document.querySelector('header-component');
  if (!(header instanceof HTMLElement) || !header.hasAttribute('sticky')) return;

  const headerSection = header.closest('.header-section') ?? header;
  const headerBottom = headerSection.getBoundingClientRect().bottom;
  if (headerBottom <= 0) return;

  const gap = getFilterScrollExtraGap();
  const targetTop = target.getBoundingClientRect().top;
  const overlap = headerBottom + gap - targetTop;
  if (overlap > 0) {
    window.scrollBy({ top: overlap, left: 0, behavior });
  }
}

/**
 * Mobiel sorteert met een <select>, desktop met een radiogroep, en beide horen bij hetzelfde
 * formulier. Zet het besturingselement uit dat de huidige viewport niet gebruikt, zodat
 * FormData één `sort_by` oplevert in plaats van twee.
 *
 * @param {Element | null | undefined} form
 * @returns {() => void} Zet terug wat er is uitgezet.
 */
export function suppressInactiveSortControls(form) {
  if (!(form instanceof HTMLFormElement)) return () => {};

  const isMobile = window.innerWidth < SORTING_MOBILE_BREAKPOINT;
  /** @type {(HTMLInputElement | HTMLSelectElement)[]} */
  const disabled = [];

  for (const element of document.querySelectorAll('input[name="sort_by"], select[name="sort_by"]')) {
    if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement)) continue;
    if (element.form !== form || element.disabled) continue;
    // In het thema staat dit attribuut op <sorting-filter-component>.
    if (!element.closest('[data-should-use-select-on-mobile="true"]')) continue;

    const isRadio = element instanceof HTMLInputElement;
    if (isMobile !== isRadio) continue;

    element.disabled = true;
    disabled.push(element);
  }

  return () => {
    for (const element of disabled) element.disabled = false;
  };
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /** @type {HdsFilterAppliedContext | null} */
  #pendingFilterContext = null;

  #debouncedPublishFilterApplied = debounce(
    /** @param {HdsFilterAppliedPayload} payload */
    (payload) => {
      this.#pendingFilterContext = null;
      publishHdsFilterApplied(payload);
    },
    HDS_FILTER_APPLIED_DEBOUNCE_MS
  );

  /**
   * Store readable filter interaction metadata before section render.
   * @param {HdsFilterAppliedContext} context
   */
  setPendingFilterContext(context) {
    this.#pendingFilterContext = {
      ...context,
      filter_source: context.filter_source ?? getFilterSource(this),
    };
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData) {
    let newParameters;

    if (formData) {
      newParameters = new URLSearchParams(/** @type any */ (formData));
    } else {
      const form = this.refs.facetsForm;
      const restoreSortControls = suppressInactiveSortControls(form);
      try {
        newParameters = new URLSearchParams(/** @type any */ (new FormData(form)));
      } finally {
        restoreSortControls();
      }
    }

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   * @param {{ skipFilterAnalytics?: boolean }} [options]
   */
  updateFilters = (options = {}) => {
    if (this.closest('[data-hds-algolia-search-results], [data-hds-algolia-collection-results]')) {
      return;
    }

    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));

    // A collection past Shopify's 5.000-product filter limit has no server-side filtering
    // to render — re-rendering the section would hand back all 15.919 products. The URL
    // and the event above still fire; facet-index.js answers them (SHO-9684).
    if (this.#usesFacetIndex) return;

    this.#updateSection({ skipFilterAnalytics: Boolean(options.skipFilterAnalytics) });
  };

  /** @returns {boolean} */
  get #usesFacetIndex() {
    return Boolean(this.closest('[data-hds-facet-index]'));
  }

  /**
   * Queue debounced hds:filter_applied after section morph (results_count matches what the visitor saw).
   */
  #queueFilterAppliedAnalytics() {
    const context = this.#pendingFilterContext;
    if (!context) return;

    this.#debouncedPublishFilterApplied({
      filter_name: context.filter_name,
      filter_value: context.filter_value,
      filter_action: context.filter_action,
      results_count: readResultsCount(this.sectionId),
      collection_handle: readCollectionHandle(this),
      filter_source: context.filter_source ?? getFilterSource(this),
    });
  }

  /**
   * Updates the section
   * @param {{ skipFilterAnalytics?: boolean }} [options]
   */
  #updateSection(options = {}) {
    const sectionId = this.sectionId;
    const generation = nextFacetsSectionUpdateGeneration(sectionId);
    const inDrawer = Boolean(this.closest('dialog'));
    const viewTransition = !inDrawer;
    const skipFilterAnalytics = Boolean(options.skipFilterAnalytics);

    setSeeResultsPending(true);
    setResultsPending(sectionId, true);
    sectionRenderer.abortSection(sectionId);
    abortFacetsDrawerLoads();

    // Al voorgeladen (hover/aanraken): niet eerst op een volgende klik wachten.
    const prefetched = sectionRenderer.claimPrefetch(sectionId, facetCompanions(inDrawer).companions);

    scheduleFacetsSectionUpdate(
      sectionId,
      () => {
        void this.#runSectionUpdate({
          generation,
          inDrawer,
          skipFilterAnalytics,
          viewTransition,
        });
      },
      prefetched ? 0 : FILTER_SECTION_DEBOUNCE_MS
    );
  }

  /**
   * @param {{
   *   generation: number,
   *   inDrawer: boolean,
   *   skipFilterAnalytics: boolean,
   *   viewTransition: boolean,
   * }} params
   */
  async #runSectionUpdate({ generation, inDrawer, skipFilterAnalytics, viewTransition }) {
    const sectionId = this.sectionId;
    if (!isCurrentFacetsSectionUpdate(sectionId, generation)) return;

    // De filterlijst (mobiele drawer of desktopkolom) komt in hetzelfde verzoek mee. Eerst
    // morphen en dan de lijst ophalen kostte een tweede rondgang, met een lege kolom ertussen.
    // Ook bij filteren in de drawer: op desktop staat de kolom erachter, en die slaat de morph over.
    const { drawerRoot, drawerSection, verticalSection, companions } = facetCompanions(inDrawer);

    // Eerst ophalen, dan pas de view transition: die bevriest de pagina, en met de fetch erin
    // stond het oude raster de hele serverrender stil (SHO-11729).
    /** @type {Record<string, string | null> | null} */
    let parts;
    try {
      parts = await sectionRenderer.fetchSections(sectionId, companions);
    } catch (error) {
      if (isCurrentFacetsSectionUpdate(sectionId, generation)) {
        setSeeResultsPending(false);
        setResultsPending(sectionId, false);
      }
      throw error;
    }
    // null = afgebroken door een nieuwere klik (geen stale morph/drawer-write).
    if (!parts || !isCurrentFacetsSectionUpdate(sectionId, generation)) return;

    const html = /** @type {string} */ (parts[normalizeSectionId(sectionId)]);
    const drawerHtml = drawerSection ? parts[drawerSection] ?? null : null;
    const verticalHtml = verticalSection ? parts[verticalSection] ?? null : null;

    const afterRender = async () => {
      if (!isCurrentFacetsSectionUpdate(sectionId, generation)) return;

      if (inDrawer) {
        if (drawerRoot instanceof HTMLElement && drawerRoot.isConnected) {
          await loadFacetsDrawerContent(drawerRoot, { force: true, html: drawerHtml ?? undefined });
        }
        restoreOpenFacetsSheet();
      }

      if (!isCurrentFacetsSectionUpdate(sectionId, generation)) return;

      syncFacetsSeeResultsCount(sectionId);

      // De drawer schrijft hierboven zijn eigen innerHTML en knipt daarmee op mobiel juist
      // dit component los. Publiceren moet dus vóór de isConnected-guard, anders meldt
      // filteren op mobiel niets meer (de guard staat er voor het scrollen).
      if (!skipFilterAnalytics) {
        this.#queueFilterAppliedAnalytics();
      }

      if (!this.isConnected) return;

      this.#scrollToResultsList();
    };

    const run = async () => {
      // Tussen fetch en morph kan nog een nieuwere klik zijn gestart (wachten op de transition).
      if (!isCurrentFacetsSectionUpdate(sectionId, generation)) return;
      try {
        await morphSection(sectionId, html);
      } finally {
        // De morph laat classes op de sectie-wrapper staan; zelf weghalen.
        setResultsPending(sectionId, false);
      }
      applyVerticalFacets(verticalHtml);

      await afterRender();
    };

    if (viewTransition) {
      startViewTransition(() => run(), ['product-grid']);
    } else {
      void run();
    }
  }

  /**
   * Scrolls to the product grid below the sticky header after filter updates.
   */
  #scrollToResultsList() {
    const target = getResultsListTarget(this.sectionId);
    if (!target) return;

    const behavior = getFilterScrollBehavior();

    requestAnimationFrame(() => {
      const rect = target.getBoundingClientRect();
      const top = Math.max(0, rect.top + window.scrollY - getFilterScrollOffset());
      window.scrollTo({ top, left: 0, behavior });

      const runNudge = () => nudgeScrollBelowStickyHeader(target, behavior);

      if (behavior === 'instant') {
        requestAnimationFrame(runNudge);
        return;
      }

      if ('onscrollend' in window) {
        const onScrollEnd = () => {
          window.removeEventListener('scrollend', onScrollEnd);
          runNudge();
        };
        window.addEventListener('scrollend', onScrollEnd, { once: true });
        return;
      }

      setTimeout(runNudge, 450);
    });
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   * @param {{ skipFilterAnalytics?: boolean }} [options]
   */
  updateFiltersByURL(url, options = {}) {
    if (this.closest('[data-hds-algolia-search-results], [data-hds-algolia-collection-results]')) {
      return;
    }

    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));

    if (this.#usesFacetIndex) return;

    this.#updateSection({ skipFilterAnalytics: Boolean(options.skipFilterAnalytics) });
  }
}

if (!customElements.get('facets-form-component')) {
  if (!customElements.get('facets-form-component')) customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @param {Event} [event]
   */
  updateFilters(event) {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const input = resolveFacetInputFromEvent(event);
    if (input instanceof HTMLInputElement && input.name.startsWith('filter.')) {
      facetsForm.setPendingFilterContext({
        filter_name: getFacetFilterName(input),
        filter_value: getFacetFilterValueFromInput(input),
        filter_action: input.checked ? 'add' : 'remove',
      });
    }

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters(/** @type {Event} */ ({ target: closestInput }));
    }
  }

  /**
   * Haalt alvast het resultaat op van een klik op deze filteroptie, via dezelfde route als de
   * klik zelf (raster plus filterlijst in één verzoek). Met de muis na even stilstaan, bij
   * aanraken meteen: daar zit maar ±100 ms tussen aanraken en de klik. SHO-11729.
   * @param {PointerEvent} event - The pointerenter event
   */
  prefetchPage = (event) => {
    if (event.pointerType === 'mouse') this.#prefetchOnHover(event);
    else this.#prefetch(event);
  };

  #prefetchOnHover = debounce((/** @type {PointerEvent} */ event) => this.#prefetch(event), 100);

  cancelPrefetchPage = () => this.#prefetchOnHover.cancel();

  /** @param {Event} event */
  #prefetch(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Prefetching the section is pointless where facet-index.js owns the grid (SHO-9684).
    if (this.closest('[data-hds-facet-index]')) return;

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const inputElement = event.target.querySelector('input');
    if (!(inputElement instanceof HTMLInputElement)) return;

    // De stand ná de klik even nabootsen, zodat de URL precies is wat updateFilters() maakt.
    // Synchroon terugzetten: er komt geen event en geen paint tussen.
    const checked = inputElement.checked;
    inputElement.checked = !checked;
    let urlParameters;
    try {
      urlParameters = facetsForm.createURLParameters();
    } finally {
      inputElement.checked = checked;
    }

    const url = new URL(window.location.href);
    url.search = '';
    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    const { companions } = facetCompanions(Boolean(facetsForm.closest('dialog')));
    sectionRenderer.prefetchSections(facetsForm.sectionId, companions, url);
  }

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  if (!customElements.get('facet-inputs-component')) customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const min = minInput.value.trim();
    const max = maxInput.value.trim();
    const isClear = !min && !max;
    facetsForm.setPendingFilterContext({
      filter_name: getFacetFilterName(this),
      filter_value: isClear ? '' : `${min || '0'}–${max || ''}`,
      filter_action: isClear ? 'clear' : 'add',
    });

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  if (!customElements.get('price-facet-component')) customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.setPendingFilterContext({
      filter_name: getFacetFilterName(event.target),
      filter_value: '',
      filter_action: 'clear',
    });
    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  if (!customElements.get('facet-clear-component')) customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.setPendingFilterContext(getFilterContextFromRemoveComponent(this, event));
    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  if (!customElements.get('facet-remove-component')) customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // Select (mobiel) en radiogroep (desktop) delen één formulier; het inactieve paar wordt
    // in createURLParameters uitgezet, zodat elke route één `sort_by` in de URL zet.
    facetsForm.updateFilters({ skipFilterAnalytics: true });
    this.updateFacetStatus(event);

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  if (!customElements.get('sorting-filter-component')) customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  if (!customElements.get('facet-status-component')) customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

/**
 * @param {string} value
 * @returns {string}
 */
function escapeAttrSelector(value) {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
const FACETS_DRAWER_MAIN_TOKEN = '__HDS_FACETS_MAIN_SECTION__';

/** @type {{ mode: string, label: string, opener: HTMLElement | null } | null} */
let pendingFacetsSheet = null;

/**
 * Last committed sheet (chip vs all-filters). Morph of `#filters-drawer` strips
 * `data-facets-sheet-mode` from the server HTML, and a filter click then reloads
 * the lazy body with every group visible — without this, the open chip-sheet
 * jumps to the all-filters drawer.
 *
 * @type {{ mode: string, label: string }}
 */
let activeFacetsSheet = { mode: 'all', label: '' };

/**
 * Chip subtitle: first selected value, or `First +N` when several are on.
 *
 * @param {string[]} activeLabels
 * @returns {string}
 */
export function facetChipSubtitle(activeLabels) {
  const labels = Array.isArray(activeLabels) ? activeLabels.filter(Boolean) : [];
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels[0]} +${labels.length - 1}`;
}

/**
 * @param {HTMLElement} drawer
 * @param {string} mode
 */
function syncFacetsSheetClearButton(drawer, mode) {
  const clearButton = drawer.querySelector('[data-facets-sheet-clear]');
  const clearLabel = drawer.querySelector('[data-facets-sheet-clear-label]');
  if (!(clearButton instanceof HTMLButtonElement)) return;

  const isAll = !mode || mode === 'all';
  const label = isAll ? drawer.dataset.facetsClearAll : drawer.dataset.facetsClearGroup;
  if (clearLabel && label && clearLabel.textContent !== label) clearLabel.textContent = label;
  if (label && clearButton.getAttribute('aria-label') !== label) clearButton.setAttribute('aria-label', label);

  let hasSelection = false;
  if (isAll) {
    hasSelection = Boolean(drawer.querySelector('.facets__inputs-list input:checked, .facets__clear-all.active'));
    for (const input of drawer.querySelectorAll('.price-facet input[type="number"]')) {
      if (input instanceof HTMLInputElement && input.value !== '') hasSelection = true;
    }
  } else {
    const item = drawer.querySelector(`.facets__item[data-filter-param-name="${escapeAttrSelector(mode)}"]`);
    hasSelection = Boolean(item?.querySelector('input:checked'));
    if (item?.querySelector('.price-facet')) {
      for (const input of item.querySelectorAll('input[type="number"]')) {
        if (input instanceof HTMLInputElement && input.value !== '') hasSelection = true;
      }
    }
  }

  clearButton.disabled = !hasSelection;
}

/**
 * Show every facet group, or only the group that matches `mode`.
 *
 * @param {HTMLElement} drawer
 * @param {string} [mode]
 * @param {{ label?: string }} [options]
 */
export function applyFacetsSheetMode(drawer, mode = 'all', options = {}) {
  if (!(drawer instanceof HTMLElement)) return;

  const sheet = mode && mode !== 'all' ? mode : 'all';
  const label = sheet === 'all' ? '' : options.label || drawer.dataset.facetsSheetLabel || activeFacetsSheet.label || '';

  drawer.dataset.facetsSheetMode = sheet;
  if (label) drawer.dataset.facetsSheetLabel = label;
  else delete drawer.dataset.facetsSheetLabel;

  activeFacetsSheet = { mode: sheet, label };

  const title = drawer.querySelector('.facets-drawer__title-text');
  const allTitle = drawer.dataset.facetsSheetAllTitle || '';
  const nextTitle = sheet === 'all' ? allTitle : label || allTitle;
  if (title && title.textContent.trim() !== nextTitle) {
    title.textContent = nextTitle;
  }

  for (const item of drawer.querySelectorAll('.facets__item')) {
    if (!(item instanceof HTMLElement)) continue;
    const param = item.getAttribute('data-filter-param-name') || '';
    const show = sheet === 'all' || param === sheet;
    item.hidden = !show;

    const lock = show && sheet !== 'all';
    const details = item.querySelector('details');
    const summary = item.querySelector('summary');

    if (lock && details instanceof HTMLDetailsElement) details.open = true;

    if (summary instanceof HTMLElement) {
      if (lock) {
        summary.tabIndex = -1;
        summary.setAttribute('aria-disabled', 'true');
      } else {
        summary.removeAttribute('tabindex');
        summary.removeAttribute('aria-disabled');
      }
    }
  }

  bindFacetsSheetAccordionLock(drawer);
  syncFacetsSheetClearButton(drawer, sheet);
}

/**
 * Re-apply the sheet the visitor opened, even if morph wiped the dataset.
 *
 * @param {HTMLElement} drawer
 */
export function restoreFacetsSheetMode(drawer) {
  const mode = activeFacetsSheet.mode || drawer.dataset.facetsSheetMode || 'all';
  const label = activeFacetsSheet.label || drawer.dataset.facetsSheetLabel || '';
  applyFacetsSheetMode(drawer, mode, { label });
}

/**
 * @param {HTMLElement} [drawer]
 */
function restoreOpenFacetsSheet(drawer) {
  const host = drawer instanceof HTMLElement ? drawer : document.querySelector('#filters-drawer');
  if (!(host instanceof HTMLElement)) return;

  const dialog = host.querySelector('dialog');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  restoreFacetsSheetMode(host);
}

/**
 * @param {boolean} expanded
 * @param {HTMLElement | null} [opener]
 */
function setFacetsSheetExpanded(expanded, opener = null) {
  for (const button of document.querySelectorAll('[data-facets-sheet]')) {
    if (!(button instanceof HTMLElement)) continue;
    const isOpener = opener != null && button === opener;
    button.setAttribute('aria-expanded', isOpener && expanded ? 'true' : 'false');
  }
}

/**
 * @param {HTMLElement} drawer
 */
function clearFacetsSheetSelection(drawer) {
  const mode = drawer.dataset.facetsSheetMode || 'all';

  if (mode === 'all') {
    const clearAll = drawer.querySelector('.facets__clear-all');
    if (clearAll instanceof HTMLElement) {
      clearAll.click();
      return;
    }
  }

  const item =
    mode === 'all'
      ? drawer
      : drawer.querySelector(`.facets__item[data-filter-param-name="${escapeAttrSelector(mode)}"]`);
  if (!(item instanceof Element)) return;

  for (const input of item.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked')) {
    if (!(input instanceof HTMLInputElement) || input.disabled) continue;
    input.checked = false;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  for (const input of item.querySelectorAll('.price-facet input[type="number"]')) {
    if (!(input instanceof HTMLInputElement) || input.value === '') continue;
    input.value = '';
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * Chip sheets show one group as the whole drawer — folding that accordion
 * would empty the sheet. All-filters keeps the normal expand/collapse.
 *
 * @param {HTMLElement} drawer
 */
function bindFacetsSheetAccordionLock(drawer) {
  if (drawer.dataset.facetsSheetAccordionBound === 'true') return;
  drawer.dataset.facetsSheetAccordionBound = 'true';
  drawer.addEventListener(
    'click',
    (event) => {
      const mode = drawer.dataset.facetsSheetMode;
      if (!mode || mode === 'all') return;
      const summary = event.target instanceof Element ? event.target.closest('summary') : null;
      if (!(summary instanceof HTMLElement) || !drawer.contains(summary)) return;
      event.preventDefault();
    },
    true,
  );
}

/**
 * @param {HTMLElement} drawer
 */
function bindFacetsSheetClear(drawer) {
  const clearButton = drawer.querySelector('[data-facets-sheet-clear]');
  if (!(clearButton instanceof HTMLElement) || clearButton.dataset.facetsSheetClearBound === 'true') return;
  clearButton.dataset.facetsSheetClearBound = 'true';
  clearButton.addEventListener('click', () => {
    if (clearButton instanceof HTMLButtonElement && clearButton.disabled) return;
    clearFacetsSheetSelection(drawer);
  });
}

/** @type {WeakMap<HTMLElement, Promise<void>>} */
const facetsDrawerLoads = new WeakMap();

/**
 * Wait until the bottom-drawer open animation is idle so innerHTML cannot hitch the slide.
 *
 * @param {HTMLElement} root
 * @returns {Promise<void>}
 */
async function waitForFacetsDrawerOpenAnimation(root) {
  const drawer = root.closest('#filters-drawer');
  const dialog = drawer instanceof HTMLElement ? drawer.querySelector('dialog') : null;
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;
  if (dialog.classList.contains('dialog-closing') || dialog.classList.contains('drawer-dragging')) return;
  if (typeof dialog.getAnimations !== 'function') return;

  const running = dialog
    .getAnimations({ subtree: false })
    .filter((animation) => animation.playState === 'running');
  if (running.length === 0) return;

  await Promise.all(running.map((animation) => animation.finished.catch(() => {})));
}

/**
 * If the drawer is mid-close, wait until it is closed before writing the DOM.
 *
 * @param {HTMLElement} root
 * @returns {Promise<void>}
 */
function waitUntilFacetsDrawerIdleForDomWrite(root) {
  const drawer = root.closest('#filters-drawer');
  const dialog = drawer instanceof HTMLElement ? drawer.querySelector('dialog') : null;
  if (!(dialog instanceof HTMLDialogElement)) return Promise.resolve();
  if (!dialog.open || !dialog.classList.contains('dialog-closing')) return Promise.resolve();
  if (!(drawer instanceof HTMLElement)) return Promise.resolve();

  return new Promise((resolve) => {
    const onClose = () => {
      drawer.removeEventListener(DialogCloseEvent.eventName, onClose);
      resolve();
    };
    drawer.addEventListener(DialogCloseEvent.eventName, onClose, { once: true });
  });
}

/**
 * Lazy-loads or refreshes the mobile filter drawer body from collection-facets-drawer.
 *
 * @param {HTMLElement} root - `[data-facets-drawer-root]`
 * @param {{ force?: boolean, html?: string }} [options] - `html`: al opgehaalde sectie (filterklik), dan geen eigen fetch
 * @returns {Promise<void>}
 */
export async function loadFacetsDrawerContent(root, { force = false, html: prefetchedHtml } = {}) {
  const mainSectionId = root.dataset.mainSectionId;
  const drawerSectionId = root.dataset.facetsDrawerSectionId || 'collection-facets-drawer';
  if (!mainSectionId) return;

  const loadedKey = `${window.location.pathname}${window.location.search}`;
  if (!force && root.dataset.loadedKey === loadedKey && root.querySelector('facets-form-component')) {
    return;
  }

  const inFlight = facetsDrawerLoads.get(root);
  if (!force && inFlight) return inFlight;

  if (force) {
    abortFacetsDrawerLoads();
  }

  const abortController = new AbortController();
  facetsDrawerAbortControllers.add(abortController);

  /** @type {Promise<void>} */
  let run;
  run = (async () => {
    const loading = root.querySelector('.facets-drawer__lazy-loading');
    let wroteDom = false;

    try {
      let html = prefetchedHtml;
      if (html == null) {
        const fetchUrl = buildSectionRenderingURL(drawerSectionId);
        const response = await fetch(fetchUrl, { signal: abortController.signal });
        if (!response.ok) throw new Error(`Facets drawer fetch failed: ${response.status}`);
        html = await response.text();
      }
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const sectionEl = parsed.querySelector('[id^="shopify-section-"]');

      if (!(sectionEl instanceof HTMLElement)) {
        throw new Error('Facets drawer: no shopify-section in response');
      }

      let innerHtml = sectionEl.innerHTML;
      if (!innerHtml.trim()) return;

      // innerHTML tijdens slide-in/out laat height/layout de transform onderbreken.
      if (!force) {
        await waitForFacetsDrawerOpenAnimation(root);
        await waitUntilFacetsDrawerIdleForDomWrite(root);
      }

      if (abortController.signal.aborted) return;

      loading?.classList.remove('facets-drawer__lazy-loading--pending');
      innerHtml = innerHtml.split(FACETS_DRAWER_MAIN_TOKEN).join(mainSectionId);
      root.innerHTML = innerHtml;
      wroteDom = true;
      root.dataset.loadedKey = `${window.location.pathname}${window.location.search}`;

      const drawerEl = root.closest('#filters-drawer');
      if (drawerEl instanceof HTMLElement) {
        restoreFacetsSheetMode(drawerEl);
        bindFacetsSheetClear(drawerEl);
      }

      const sectionId =
        root.dataset.mainSectionId ||
        drawerEl?.closest('results-list')?.getAttribute('section-id') ||
        '';
      if (sectionId) syncFacetsSeeResultsCount(sectionId);

      const resultsList =
        drawerEl instanceof HTMLElement
          ? drawerEl.closest('results-list[data-hds-algolia-search-results], results-list[data-hds-algolia-collection-results]')
          : null;
      if (resultsList instanceof HTMLElement) {
        document.dispatchEvent(
          new CustomEvent('hds:facets-drawer-loaded', {
            bubbles: true,
            detail: { root, host: resultsList },
          }),
        );
      }
    } catch (err) {
      if (isAbortError(err) || abortController.signal.aborted) return;
      console.error(err);
      window.Sentry?.captureException?.(err);
      if (!wroteDom) {
        root.innerHTML = `<div class="facets-drawer__lazy-loading facets-drawer__lazy-loading--error" role="alert"></div>`;
      }
    } finally {
      facetsDrawerAbortControllers.delete(abortController);
      if (facetsDrawerLoads.get(root) === run) {
        facetsDrawerLoads.delete(root);
      }
    }
  })();

  facetsDrawerLoads.set(root, run);
  return run;
}

function initFacetsDrawerLazyLoad() {
  const designMode = Boolean(/** @type {{ designMode?: boolean }} */ (window.Shopify)?.designMode);

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const opener = target.closest('[data-facets-sheet]');
      if (!(opener instanceof HTMLElement)) return;

      const drawer = document.querySelector('#filters-drawer');
      const dialog = drawer instanceof HTMLElement ? drawer.querySelector('dialog') : null;
      if (dialog instanceof HTMLDialogElement && dialog.open) return;

      pendingFacetsSheet = {
        mode: opener.dataset.facetsSheet || 'all',
        label: opener.dataset.facetsSheetLabel || '',
        opener,
      };

      // Sheet-mode + prefetch vóór showModal, terwijl de dialog nog dicht is.
      if (drawer instanceof HTMLElement) {
        applyFacetsSheetMode(drawer, pendingFacetsSheet.mode, { label: pendingFacetsSheet.label });
        const root = drawer.querySelector('[data-facets-drawer-root]');
        if (!designMode && root instanceof HTMLElement) {
          void loadFacetsDrawerContent(root);
        }
      }
    },
    true,
  );

  document.addEventListener(
    DialogOpenEvent.eventName,
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const drawer = target.closest('#filters-drawer');
      if (!(drawer instanceof HTMLElement)) return;

      if (pendingFacetsSheet) {
        setFacetsSheetExpanded(true, pendingFacetsSheet.opener);
      }

      const root = drawer.querySelector('[data-facets-drawer-root]');
      if (!designMode && root instanceof HTMLElement) {
        // Niet await-en: fetch mag overlappen met de slide, DOM-inject wacht op animatie-einde.
        void loadFacetsDrawerContent(root).then(() => {
          if (!document.contains(drawer)) return;
          bindFacetsSheetClear(drawer);
        });
        return;
      }

      restoreFacetsSheetMode(drawer);
      bindFacetsSheetClear(drawer);
    },
    true
  );

  document.addEventListener(DialogCloseEvent.eventName, (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const drawer = target.closest('#filters-drawer');
    if (!(drawer instanceof HTMLElement)) return;
    pendingFacetsSheet = null;
    activeFacetsSheet = { mode: 'all', label: '' };
    applyFacetsSheetMode(drawer, 'all');
    setFacetsSheetExpanded(false);
  });

  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    restoreOpenFacetsSheet();
    requestAnimationFrame(() => {
      restoreOpenFacetsSheet();
      setTimeout(restoreOpenFacetsSheet, 0);
    });
  });

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('[data-facets-drawer-root]').forEach((el) => {
      if (el instanceof HTMLElement) el.removeAttribute('data-loaded-key');
    });
  });
}

initFacetsDrawerLazyLoad();
